import React, { useEffect, useRef, useState } from 'react';
import { Button } from '../designSystem';
import Notice from './ui/Notice';

const CaptchaVerification = ({
  appKey,
  sceneId,
  onSuccess,
  onError,
  onCancel,
  embedded = false
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const captchaRef = useRef(null);
  const captchaInstanceRef = useRef(null);

  useEffect(() => {
    if (!appKey || !sceneId) {
      setError('验证参数缺失');
      setLoading(false);
      return;
    }

    // Load Alibaba Cloud CAPTCHA script
    const script = document.createElement('script');
    script.src = 'https://o.alicdn.com/captcha-frontend/aliyunCaptcha/AliyunCaptcha.js';
    script.async = true;
    script.onload = () => {
      initializeCaptcha();
    };
    script.onerror = () => {
      setError('验证组件加载失败');
      setLoading(false);
    };

    document.body.appendChild(script);

    return () => {
      // Cleanup
      if (captchaInstanceRef.current) {
        try {
          captchaInstanceRef.current.reset();
        } catch (e) {
          console.error('Failed to reset captcha:', e);
        }
      }
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appKey, sceneId]);

  const initializeCaptcha = () => {
    if (!window.initAliyunCaptcha) {
      setError('验证组件初始化失败');
      setLoading(false);
      return;
    }

    try {
      // Set global config
      window.AliyunCaptchaConfig = {
        region: 'sgp',
        prefix: appKey,
      };

      const captchaElement = document.createElement('div');
      captchaElement.id = `captcha-element-${Date.now()}`;

      const buttonElement = document.createElement('button');
      buttonElement.id = `captcha-button-${Date.now()}`;
      buttonElement.style.display = 'none';

      if (captchaRef.current) {
        captchaRef.current.appendChild(captchaElement);
        captchaRef.current.appendChild(buttonElement);
      }

      window.initAliyunCaptcha({
        SceneId: sceneId,
        mode: embedded ? 'embed' : 'popup',
        element: `#${captchaElement.id}`,
        button: `#${buttonElement.id}`,
        success: function(captchaVerifyParam) {
          setLoading(false);
          if (onSuccess) {
            onSuccess(captchaVerifyParam);
          }
        },
        fail: function(error) {
          setError('验证失败，请重试');
          console.error('CAPTCHA Failed:', error);
        },
        getInstance: function(instance) {
          captchaInstanceRef.current = instance;
          setLoading(false);
          // Auto-trigger for popup mode
          if (!embedded) {
            setTimeout(() => {
              buttonElement.click();
            }, 100);
          }
        },
        slideStyle: {
          width: 320,
          height: 40,
        },
        language: 'cn',
        timeout: 10000,
        onError: function(error) {
          console.error('CAPTCHA Error:', error);
          setError('验证加载失败，请刷新页面重试');
          setLoading(false);
          if (onError) {
            onError(error.message || '验证加载失败');
          }
        },
      });
    } catch (e) {
      console.error('Failed to initialize CAPTCHA:', e);
      setError('验证初始化失败');
      setLoading(false);
    }
  };

  const containerStyle = embedded
    ? {
        padding: 14,
        border: '1px solid var(--line-1)',
        borderRadius: 'var(--radius-md)',
        background: 'var(--surface-sunken)',
      }
    : {
        position: 'fixed',
        inset: 0,
        background: 'var(--scrim)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      };

  const contentStyle = embedded
    ? {}
    : {
        background: 'var(--surface-card)',
        borderRadius: 'var(--radius-xl)',
        padding: 24,
        maxWidth: 400,
        width: '90%',
        boxShadow: 'var(--shadow-lg)',
      };

  return (
    <div style={containerStyle} onClick={!embedded ? onCancel : undefined}>
      <div style={contentStyle} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ fontSize: 'var(--text-md)', marginBottom: 2 }}>安全验证</h3>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-3)', marginBottom: 8 }}>
          请完成验证以继续
        </p>

        <div
          ref={captchaRef}
          style={{
            minHeight: embedded ? 60 : 200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {loading ? <span className="lt-loading-dot" aria-hidden="true" /> : null}
        </div>

        {error ? (
          <Notice variant="error" style={{ marginTop: 8 }}>
            {error}
          </Notice>
        ) : null}

        {!embedded && onCancel ? (
          <Button variant="secondary" fullWidth style={{ marginTop: 16 }} onClick={onCancel}>
            取消
          </Button>
        ) : null}
      </div>
    </div>
  );
};

export default CaptchaVerification;
